import React from 'react';
import { Tabs } from '@mantine/core';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { TemplateDetailsTab } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/EditGeneratableDocument/TemplateDetailsTab/TemplateDetailsTab.tsx';
import { SmartFieldsTab } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/EditGeneratableDocument/SmartFieldsTab/SmartFieldsTab.tsx';
import { DataGeneratableDocumentTemplateWithAllRelationsTct } from '@dealstack/shared/lib/generated';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { useParams } from 'react-router-dom';
import { GeneratableDocumentsDropdown } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/breadcrumb/GeneratableDocumentsDropdown';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { getGeneratableDocumentTemplate } from '@dealstack/frontend/shared/dataExchange/services/god.service.ts';
import { generatableDocumentEditPath } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/ManageGeneratableDocuments.utils.ts';

enum EditGeneratableDocumentTabs {
	TEMPLATE_DETAILS = 'templateDetails',
	SMART_FIELDS = 'smartFields',
}

interface Props {
	className?: string;
	documentsTemplates?: DataGeneratableDocumentTemplateWithAllRelationsTct[];
}

export const EditGeneratableDocument = ({ documentsTemplates, className }: Props) => {
	const { templateId } = useParams();

	const getGeneratableDocumentTemplateRequest = useQuery({
		queryKey: [
			API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_TEMPLATE_ID_GET,
			{ id: templateId as string },
		],
		queryFn: ({ signal }) =>
			getGeneratableDocumentTemplate(
				API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_TEMPLATE_ID_GET,
				{ id: templateId as string },
				signal
			),
	});

	const templateData = getGeneratableDocumentTemplateRequest.data;

	useBreadcrumbs([
		{
			name: templateData?.name ?? '',
			url: generatableDocumentEditPath(templateData?.id ?? ''),
			element: <GeneratableDocumentsDropdown documentsTemplates={documentsTemplates} />,
		},
	]);

	return (
		<div className={className}>
			{getGeneratableDocumentTemplateRequest.isLoading && <InlineLoader />}

			{!!templateData && (
				<Tabs defaultValue={EditGeneratableDocumentTabs.TEMPLATE_DETAILS}>
					<Tabs.List mb="2rem">
						<Tabs.Tab
							value={EditGeneratableDocumentTabs.TEMPLATE_DETAILS}
							sx={{ fontSize: '1rem' }}
						>
							Template details
						</Tabs.Tab>
						<Tabs.Tab
							value={EditGeneratableDocumentTabs.SMART_FIELDS}
							sx={{ fontSize: '1rem' }}
						>
							Smart fields
						</Tabs.Tab>
					</Tabs.List>

					<Tabs.Panel value={EditGeneratableDocumentTabs.TEMPLATE_DETAILS}>
						<TemplateDetailsTab
							templateName={templateData.name}
							templateType={templateData.type}
							templateId={templateData.id}
							refetchTemplateData={async () => {
								await getGeneratableDocumentTemplateRequest.refetch();
							}}
						/>
					</Tabs.Panel>

					<Tabs.Panel value={EditGeneratableDocumentTabs.SMART_FIELDS}>
						<SmartFieldsTab
							templateName={templateData.name}
							templateType={templateData.type}
							templateId={templateData.id}
						/>
					</Tabs.Panel>
				</Tabs>
			)}
		</div>
	);
};
